import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE } from '../AuthContext';

interface Component { name: string; status: string; latency_ms?: number | null; detail?: string; }
interface StatusPayload { status: string; components: Component[]; checked_at: string; }

const COLORS: Record<string, string> = {
  operational: 'bg-emerald-500',
  degraded: 'bg-amber-500',
  outage: 'bg-red-500',
};
const LABELS: Record<string, string> = {
  operational: 'All systems operational',
  degraded: 'Partial degradation',
  outage: 'Service outage',
};

export default function StatusPage() {
  const [data, setData] = useState<StatusPayload | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/status/public`);
        if (!res.ok) throw new Error();
        const json = await res.json();
        if (!cancelled) { setData(json); setError(false); }
      } catch { if (!cancelled) setError(true); }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const overall = error ? 'outage' : data?.status || 'operational';

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="status-page">
      <header className="border-b border-border/40">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-6 h-14">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-block w-6 h-6 rounded-md bg-primary" />
            <span className="font-semibold tracking-tight">CloudGenie · Status</span>
          </Link>
          <Link to="/docs" className="text-sm text-muted-foreground hover:text-foreground">Docs</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-14">
        <div className={`rounded-xl p-6 flex items-center gap-4 ${overall === 'operational' ? 'bg-emerald-950/40 border border-emerald-800/40' : overall === 'degraded' ? 'bg-amber-950/40 border border-amber-800/40' : 'bg-red-950/40 border border-red-800/40'}`}
             data-testid="status-banner">
          <span className={`h-3.5 w-3.5 rounded-full ${COLORS[overall]} animate-pulse`} />
          <div>
            <p className="text-xl font-semibold" data-testid="status-overall">
              {error ? 'Unable to reach the API' : LABELS[overall]}
            </p>
            {data && <p className="text-xs text-muted-foreground mt-0.5">Last checked {new Date(data.checked_at).toLocaleTimeString()} · auto-refreshes every 60s</p>}
          </div>
        </div>

        <div className="mt-8 space-y-3">
          {(data?.components || []).map((c) => (
            <div key={c.name} className="flex items-center justify-between rounded-lg border border-border bg-card px-5 py-4"
                 data-testid={`status-component-${c.name.toLowerCase().replace(/\s+/g, '-')}`}>
              <div>
                <p className="font-medium">{c.name}</p>
                {c.detail && <p className="text-xs text-muted-foreground mt-0.5">{c.detail}</p>}
              </div>
              <div className="flex items-center gap-2">
                {typeof c.latency_ms === 'number' && c.latency_ms > 0 && (
                  <span className="text-xs text-muted-foreground">{c.latency_ms} ms</span>
                )}
                <span className={`h-2.5 w-2.5 rounded-full ${COLORS[c.status] || 'bg-muted'}`} />
                <span className="text-sm capitalize">{c.status}</span>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-10 text-xs text-muted-foreground">
          <Link to="/docs/sla" className="text-primary hover:underline" data-testid="status-sla-link">View our SLA & SLOs</Link> · Incident? Email <a className="text-primary hover:underline" href="mailto:support@cloudgenie.co">support@cloudgenie.co</a> · Security reports: <a className="text-primary hover:underline" href="mailto:security@cloudgenie.co">security@cloudgenie.co</a>
        </p>
      </main>
    </div>
  );
}
